/**
 * Clubes: el MODELO y el registro. El catálogo ya no vive acá.
 *
 * Hasta ahora los clubes eran cinco constantes y `ClubId` una unión de literales, con la
 * misma garantía que `RoomId`: un club que no existe no compilaba. Eso dejó de ser
 * posible el día que los jugadores pueden **fundar** clubes — un club nace en tiempo de
 * ejecución, y ningún tipo puede conocerlo de antemano.
 *
 * Lo que se hizo en su lugar, para no perder los 195 sitios que ya preguntaban por un
 * club: `getClub()` sigue existiendo con la misma firma y el mismo contrato —devuelve el
 * club o `undefined`—, pero detrás lee un REGISTRO que cada proceso llena con lo que
 * tiene a mano. El servidor lo llena desde la base al arrancar y cada vez que un club
 * cambia; el cliente, desde `GET /api/social/clubs` al entrar. Los que preguntan no se
 * enteraron del cambio.
 *
 * El precio, dicho en voz alta: esto es **estado mutable en `shared`**, que es justo lo
 * que este paquete evita en todo lo demás. Se acepta porque la alternativa era pasar el
 * catálogo por parámetro a través de toda la pila —el dibujo de la insignia, el
 * escenario, el validador del mapa— y porque los dos procesos tienen su propia copia del
 * módulo: no hay forma de que el registro de uno contamine al otro. Lo que NO se puede
 * hacer es guardar en el registro algo que dependa de quién pregunta: es catálogo
 * público, igual que antes. Lo privado sigue saliendo por `client.send` (regla 12).
 */
/* ────────────────────────────── el registro ────────────────────────────── */
let registry = new Map();
/**
 * Reemplaza el catálogo conocido por este proceso.
 *
 * Se reemplaza entero en vez de mutar entrada por entrada: un club que desaparece de la
 * lista tiene que desaparecer del registro, y con un `set` por club los borrados se
 * quedaban para siempre.
 */
export function setClubCatalog(clubs) {
    registry = new Map(clubs.map((club) => [club.id, club]));
}
/** Todos los clubes que este proceso conoce, archivados incluidos, en orden de nombre. */
export function clubList() {
    return [...registry.values()].sort((a, b) => a.name.localeCompare(b.name, "es"));
}
/** Los que están vivos. Es lo que se enseña en el directorio y en la pestaña de clubes. */
export function activeClubs() {
    return clubList().filter((club) => club.archivedAt === null);
}
/** Los de una facultad. `null` trae los transversales, que no son de ninguna. */
export function clubsOfFaculty(facultyId) {
    return activeClubs().filter((club) => club.facultyId === facultyId);
}
/**
 * El club de un id, o `undefined`.
 *
 * Misma firma y mismo contrato que cuando el catálogo era un objeto fijo: acepta
 * `unknown` porque casi siempre se le pregunta por algo que vino de fuera.
 */
export function getClub(id) {
    return typeof id === "string" ? registry.get(id) : undefined;
}
/** ¿Este texto nombra a un club que existe? */
export function isClubId(value) {
    return getClub(value) !== undefined;
}
/** Ordena ids de club por nombre, descartando los que no existen. */
export function clubsInOrder(ids) {
    return clubList().filter((club) => ids.includes(club.id));
}
/* ────────────────────────────── papeles ────────────────────────────── */
/**
 * El papel de alguien DENTRO de un club.
 *
 * Va acá y no junto a `dev` y `admin` porque no es lo mismo: aquellas son marcas de
 * la cuenta y valen en todo el campus, y ésta sólo significa algo emparejada con un
 * club. Nadie es "presidente" a secas. Por eso vive en la fila de membresía —cuya
 * llave ya es (jugador, club)— y no en una columna de `players`, que dejaría a
 * alguien presidiendo la nada o los cinco a la vez.
 */
export const CLUB_ROLES = ["miembro", "presidente"];
export function isClubRole(value) {
    return typeof value === "string" && CLUB_ROLES.includes(value);
}
/**
 * Tope de clubes por jugador.
 *
 * Existe para que la insignia signifique algo: sin tope, entrar a todos es gratis y
 * ninguna distingue a nadie. Además acota lo que viaja en `Player.clubs`.
 */
export const MAX_CLUBS_PER_PLAYER = 3;
/* ─────────────────────── fundar: qué es un club válido ─────────────────────── */
export const MAX_CLUB_NAME = 60;
export const MIN_CLUB_NAME = 3;
export const MAX_CLUB_CODE = 12;
export const MIN_CLUB_CODE = 2;
/**
 * Tope del logo, en bytes de su fuente.
 *
 * 64 KB es holgado para un escudo —los de las asociaciones de ESPOL andan por 10— y
 * acotado para lo que es: texto guardado en una fila de SQLite, que el respaldo copia
 * entero. Se mide sobre la FUENTE (el SVG o el data URI), no sobre el archivo en disco,
 * porque es lo que de verdad se guarda.
 */
export const MAX_CLUB_LOGO_BYTES = 64 * 1024;
/** Lo que se acepta subir. El SVG se sanea en el servidor antes de guardarlo. */
export const CLUB_LOGO_TYPES = ["image/svg+xml", "image/png"];
export function clubCodeProblem(code) {
    if (typeof code !== "string" || code.trim().length === 0)
        return "empty";
    const clean = code.trim();
    if (clean.length < MIN_CLUB_CODE)
        return "too_short";
    if (clean.length > MAX_CLUB_CODE)
        return "too_long";
    return null;
}
export function clubNameProblem(name) {
    if (typeof name !== "string" || name.trim().length === 0)
        return "empty";
    const clean = name.trim();
    if (clean.length < MIN_CLUB_NAME)
        return "too_short";
    if (clean.length > MAX_CLUB_NAME)
        return "too_long";
    return null;
}
export function clubColorProblem(color) {
    if (typeof color !== "string" || color.length === 0)
        return "empty";
    return /^#[0-9a-f]{6}$/i.test(color) ? null : "bad_color";
}
/** Colores que se ofrecen al fundar. Elegir de una lista evita el gris sobre gris. */
export const CLUB_COLORS = [
    "#8fd0ff",
    "#ffb0e0",
    "#9ce8a8",
    "#c8f08a",
    "#ffd75e",
    "#ff9d7a",
    "#b7a8ff",
    "#7fe0d4",
];
/**
 * Unas siglas propuestas a partir del nombre, para rellenar el formulario.
 *
 * Sólo es una sugerencia que el que funda puede pisar: "Club de Robótica" propone "CR",
 * y si su club se llama ROBOTA, escribe ROBOTA. Existe para que nadie tenga que pensar
 * dos campos cuando uno se deduce del otro.
 */
export function suggestClubCode(name) {
    const palabras = name
        .split(/\s+/)
        .filter((palabra) => palabra.length > 0 && !MENUDAS.has(palabra.toLowerCase()));
    if (palabras.length === 0)
        return name.slice(0, MAX_CLUB_CODE).toUpperCase();
    if (palabras.length === 1)
        return palabras[0].slice(0, MAX_CLUB_CODE).toUpperCase();
    return palabras
        .map((palabra) => palabra[0])
        .join("")
        .slice(0, MAX_CLUB_CODE)
        .toUpperCase();
}
/** Palabras que no distinguen a un club de otro, así que no entran en sus siglas. */
const MENUDAS = new Set(["club", "de", "del", "la", "el", "los", "las", "y", "en"]);
//# sourceMappingURL=clubs.js.map